// src/lib/tiers.ts

export interface Tier {
  number: 1 | 2 | 3 | 4
  name: 'Attuned' | 'Listening In' | 'Urgent Conversation' | 'Priority Review'
}

const TIER_THRESHOLDS = [
  { max: 12,       number: 1, name: 'Attuned'             },
  { max: 28,       number: 2, name: 'Listening In'        },
  { max: 45,       number: 3, name: 'Urgent Conversation' },
  { max: Infinity, number: 4, name: 'Priority Review'     },
] as const

export function classifyTier(totalScore: number): Tier {
  for (const t of TIER_THRESHOLDS) {
    if (totalScore <= t.max) {
      return { number: t.number as 1|2|3|4, name: t.name }
    }
  }
  return { number: 4, name: 'Priority Review' }
}

export interface TierCopy {
  headline: string
  body: string
  ctaText: string
  ctaUrgency: 'gentle' | 'moderate' | 'strong' | 'urgent'
  doctorNote: string
}

export function getTierCopy(
  tier: Tier,
  firstName?: string,
  contraceptionStatus?: string
): TierCopy {
  const name = firstName || 'there'

  const baseCopy: Record<number, TierCopy> = {
    1: {
      headline: "Your cycle seems relatively settled — but there's more to know.",
      body: "Based on your responses, your cycle and symptoms suggest a relatively stable hormonal picture. That said, FEMM reveals that even women with mild or no symptoms often discover, through charting, that there is more going on beneath the surface than the period alone reveals.\n\nYour results suggest it may be worth exploring: ovulation confirmation and your full fertility cycle — not because something is wrong, but because knowing is always better than assuming.",
      ctaText: 'Start with FEMM charting — book an introductory session',
      ctaUrgency: 'gentle',
      doctorNote: `Hi ${name},\n\nYour results suggest a relatively settled picture — and that is genuinely good news. But I've seen many women with similar profiles discover, through FEMM charting, patterns they never knew existed. If you're curious about what your cycle is really doing each month, I'd love to show you.\n\n— Dr. Nyandia`,
    },
    2: {
      headline: "Your body has been trying to tell you something. Let's hear it.",
      body: "Your responses suggest one or more hormonal systems that may be worth investigating. The symptoms you've described are not random. In FEMM medicine, they are signals from specific hormonal pathways that are identifiable and treatable.\n\nA FEMM Hormonal Health Review would give those signals a name — and a plan.",
      ctaText: 'Book your FEMM Hormonal Health Review',
      ctaUrgency: 'moderate',
      doctorNote: `Hi ${name},\n\nI've reviewed your quiz responses and there are patterns here worth investigating. The signals your body is sending — through your cycle, your symptoms, and your energy — are the kind of things that FEMM is specifically designed to decode. I'd like to help you understand what they mean. Bring your results when you come.\n\n— Dr. Nyandia`,
    },
    3: {
      headline: "What you're experiencing has a clinical explanation. And a treatment pathway.",
      body: "Your responses paint a picture of hormonal disruption across multiple systems. The symptoms you've described are not incidental — they are the pattern that FEMM medicine is specifically designed to investigate and treat.\n\nYou've been carrying these symptoms — and in many cases, carrying them alone, without a clinical explanation. That changes with a FEMM assessment.",
      ctaText: "We'd like to see you soon — book your review",
      ctaUrgency: 'strong',
      doctorNote: `${name},\n\nYour results show a pattern I see often in my practice — multiple systems signalling at once, and no one has connected the dots. That is exactly what a FEMM consultation does. I will have reviewed your results before your appointment. When you walk in, I will already know your story. Let's get this investigated.\n\n— Dr. Nyandia`,
    },
    4: {
      headline: "Please don't wait on this one.",
      body: "Your responses indicate significant, multi-system hormonal disruption that warrants prompt clinical attention. The combination of symptoms you've described — across your cycle, your body, and your wellbeing — is not something to manage symptomatically or wait out.\n\nA FEMM Hormonal Health Review will give you a named, investigated, medically managed starting point. Not 'come back in six months.' A plan, from the first appointment.",
      ctaText: 'Book a priority appointment',
      ctaUrgency: 'urgent',
      doctorNote: `${name},\n\nI want you to know that what you're experiencing is real, it is significant, and it is investigable. I have seen your results and I would like to see you promptly. A single consultation can give us enough information to begin a plan. Please don't put this off.\n\n— Dr. Nyandia`,
    },
  }

  const copy = { ...baseCopy[tier.number] }

  if (contraceptionStatus === 'current') {
    copy.body += "\n\nNote: You are currently using hormonal contraception. This means your reported cycle symptoms may be influenced by your contraception rather than reflecting your natural hormonal state. A FEMM consultation can still assess your underlying profile — and help you understand what your contraception may be masking."
  }

  return copy
}

export const MOOD_BRAIN_COPY = {
  headline: 'The mind-body connection in your results',
  body: "Your responses indicate a significant mood and wellbeing impact alongside your physical symptoms. Research published in 2024 (del Río et al., Frontiers in Psychology) found that women with hormonal cycle disruption had 52% higher rates of depression and 71% higher rates of tension than women with healthy ovulatory cycles.\n\nThese are not separate issues. They are downstream of the same hormonal disruption that would be investigated in your consultation.",
  citation: 'del Río JP et al. (2024) Frontiers in Psychology, 15:1349484',
}
